'use client';

import { AddressFlow, EnergyType } from '@energy-retailer/constants';
import { find } from 'lodash';
import { useRouter } from 'next/navigation';
import { type Dispatch, type SetStateAction } from 'react';

import { trackAnalyticsEvent } from '@/app/lib/analytics';
import { getAddressFlow } from '@/app/lib/utils/get-address-flow';
import { RadioField } from '@/components/input';
import { Loader } from '@/components/loader';
import { Modal, FormModalContent, ModalButton } from '@/components/modal';
import { ANALYTICS_EVENTS } from '@/constants';
import { useJoinFormStore } from '@/stores/join-form-store/join-form-store';

import { MultipleAddressSchema } from './form-schema';

import type { MultipleAddressSchemaType } from './form-schema';
import type { PropertySchema } from '@energy-retailer/schema';

type MultipleAddressModalProps = {
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsFlowSwitchModalOpen?: Dispatch<SetStateAction<boolean>>;
  electricityDetails?: PropertySchema[];
  remainOnPage?: boolean;
  isLoading: boolean;
};

export const MultipleAddressModal = ({
  isModalOpen = false,
  setIsModalOpen,
  electricityDetails,
  setIsFlowSwitchModalOpen,
  remainOnPage = false,
  isLoading = false,
}: MultipleAddressModalProps) => {
  const router = useRouter();
  const {
    setPropertyDetails,
    gasPlanId,
    setCurrentStep,
    setAvailable,
    removeStep,
    resetJoinFormStore,
  } = useJoinFormStore();

  const formId = 'multiple-address-form';

  function closeModal() {
    setIsModalOpen(false);
  }

  function cancelModal() {
    setIsModalOpen(false);
    resetJoinFormStore();
  }

  function openModal() {
    setIsModalOpen(true);
  }

  return (
    <Modal
      heading="We found multiple matches for your address"
      description="We found more than one place to connect energy to your address. Please choose the correct option to get an accurate quote."
      isModalOpen={isModalOpen}
      closeModal={closeModal}
      openModal={openModal}
    >
      <FormModalContent<MultipleAddressSchemaType>
        onSubmit={(data) => {
          const selectedElectricityProperty = find(
            electricityDetails,
            ({ maskedNmi }) => maskedNmi === data.electricityAddress
          );

          if (selectedElectricityProperty) {
            const addressFlow = getAddressFlow({
              data: [selectedElectricityProperty],
            });
            setPropertyDetails(selectedElectricityProperty);

            if (addressFlow === AddressFlow.SingleNoGas) {
              setAvailable(EnergyType.Gas, false);
              removeStep(EnergyType.Gas);
            }

            if (!remainOnPage) {
              if (addressFlow === AddressFlow.Single) {
                router.push('/compare-energy-plans');
              } else {
                trackAnalyticsEvent(
                  ANALYTICS_EVENTS.multipleAddressModal.postcodeSearch,
                  {
                    newPostcode: selectedElectricityProperty.address.postcode,
                  }
                );

                if (gasPlanId && setIsFlowSwitchModalOpen) {
                  setIsFlowSwitchModalOpen(true);
                } else {
                  setCurrentStep(EnergyType.Electricity);
                  router.push('/compare-energy-plans');
                }
              }
            }

            closeModal();
          }
        }}
        schema={MultipleAddressSchema}
        id={formId}
        buttonComponent={
          <>
            <ModalButton
              variant="primary"
              type="submit"
              form={formId}
            >
              Continue
            </ModalButton>

            <ModalButton
              variant="tertiary"
              onClick={cancelModal}
            >
              Cancel
            </ModalButton>
          </>
        }
      >
        {electricityDetails?.length ? (
          <div>
            <RadioField<MultipleAddressSchemaType>
              showHeading
              name="electricityAddress"
              heading="Electricity"
              options={electricityDetails.map((detail) => ({
                id: detail.maskedNmi!,
                name: detail.address.full,
                description: `NMI: ${detail.maskedNmi}`,
                disabled: false,
              }))}
              defaultValue=""
            />
          </div>
        ) : null}
        {isLoading && <Loader />}
      </FormModalContent>
    </Modal>
  );
};
